import { Table } from 'components/table'
import { ITableHeadDTO } from 'data/dtos/components/i-table-head-dto'
import { IEmailDTO } from 'data/dtos/operation/i-email-dto'
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react'
import api from 'services/api'
import { useAlreadyMounted } from 'utils/use-already-mounted'
import { useStyles } from './styles'

const headCells: ITableHeadDTO[] = [
  {
    id: 'identifier',
    label: 'Identificador',
    width: 4,
    type: 'text',
  },
  {
    id: 'title',
    label: 'Título',
    width: 6,
    type: 'text',
  },
]

const EmailList: React.FC = () => {
  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [rowsCount, setRowsCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [rowsPerPage, setRowsPerPage] = useState<number>(100)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [columnOrder, setColumnOrder] = useState<('ASC' | 'DESC')[]>([])
  const [recordToDelete, setRecordToDelete] = useState<string | null>('')
  const [orderByDirection, setOrderByDirection] = useState<boolean>(true)
  const [emailsList, setEmailsList] = useState<IEmailDTO[]>([])

  const classes = useStyles()
  const alreadyMounted = useAlreadyMounted()

  const loadEmails = async (isSearch?: boolean) => {
    if (!isSearch) {
      setIsLoading(true)
    }

    await api
      .post('/emails/list', { search, page, rowsPerPage, columnOrder })
      .then((response) => setEmailsList(response.data.data))
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
      })

    await api
      .post('/emails/count', { search })
      .then((response) => setRowsCount(response.data.data.count))
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
      })

    setIsLoading(false)
  }

  const handleDelete = async () => {
    await api
      .delete(`/emails/${recordToDelete}`)
      .then(async () => await loadEmails())
      .catch((error) => console.log(error.response?.data))
  }

  const searchChange = (value: string) => {
    setSearch(value)
  }

  const handleClose = () => {
    setModalIsOpen(false)
  }

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    event!.preventDefault()
    setPage(newPage)
  }

  const handleChangeRowsPerPage = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCloseDelete = () => {
    setModalIsOpen(false)
    handleDelete()
  }

  const handleDeleteButton = (e: MouseEvent<any, any>, id: string) => {
    e.stopPropagation()
    setModalIsOpen(true)
    setRecordToDelete(id)
  }

  const handleSortChange = (index: number) => {
    columnOrder[index] = columnOrder[index] === 'ASC' ? 'DESC' : 'ASC'

    setColumnOrder(columnOrder)
    setOrderByDirection(!orderByDirection)
  }

  const tabOptions = [
    {
      title: 'Todas',
      size: rowsCount,
      handleClick: () => {},
    },
  ]

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!alreadyMounted) {
        loadEmails()
      }
    }, 1000)

    return () => clearTimeout(timeout)
  }, [search])

  useEffect(() => {
    loadEmails()
  }, [orderByDirection, rowsPerPage, page])

  return (
    <Table.Root>
      <Table.Info
        title='E-Mails'
        route='/emails/new'
        subTitle='Dados sobre os e-mails da plataforma.'
      />

      <Table.tabs options={tabOptions} />
      <Table.SearchBar
        value={search}
        handleChange={searchChange}
      />

      <Table.Overlay
        isEmpty={emailsList.length <= 0}
        isSearch={search.length > 0}
        isLoading={isLoading}
        route='/emails/new'
        emptyRouteText='Adicionar email'
        searchRouteText='Adicionar email'
        searchMessage='Nenhum email encontrado, você pode criar um novo email clicando no botão'
        emptyMessage='Seja bem-vindo à página de gerenciamento de Emailss. Até o momento, nenhum email foi criado. Para começar, você pode criar um novo email clicando no botão'
      >
        <Table.Wrapper
          totalRows={rowsCount}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          page={page}
          handleClose={handleClose}
          handleCloseDelete={handleCloseDelete}
          modalIsOpen={modalIsOpen}
        >
          <Table.Header
            handleSortChange={handleSortChange}
            setOrder={setColumnOrder}
            headCells={headCells}
            order={columnOrder}
          />
          <Table.Body
            handleDeleteButton={handleDeleteButton}
            editRoutePath='/emails/edit'
            headCells={headCells}
            rows={emailsList}
          />
        </Table.Wrapper>
      </Table.Overlay>
    </Table.Root>
  )
}

export default EmailList
